import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  Autocomplete,
  Alert,
  CircularProgress
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { inviteService } from '../../services/invites';
import { supabase } from '../../services/supabase';

const InviteUserDialog = ({ open, onClose, onInviteSent }) => {
  const [email, setEmail] = useState('');
  const [defaultRole, setDefaultRole] = useState('user');
  const [competitionAccess, setCompetitionAccess] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingCompetitions, setLoadingCompetitions] = useState(true);

  useEffect(() => {
    if (open) {
      loadCompetitions();
    }
  }, [open]);

  const loadCompetitions = async () => {
    try {
      setLoadingCompetitions(true);
      const { data, error } = await supabase
        .from('competitions')
        .select('id, name, status')
        .neq('status', 'deleted')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch real names from competition_configs
      const competitionIds = (data || []).map(c => c.id);
      if (competitionIds.length > 0) {
        const { data: configsData } = await supabase
          .from('competition_configs')
          .select('competition_id, content')
          .in('competition_id', competitionIds)
          .order('revision', { ascending: false });

        const nameMap = {};
        (configsData || []).forEach(config => {
          if (!nameMap[config.competition_id]) {
            nameMap[config.competition_id] = config.content?.competition?.name;
          }
        });

        const compsWithNames = (data || []).map(comp => ({
          ...comp,
          displayName: nameMap[comp.id] || comp.name || `Competition ${comp.id}`
        }));
        setCompetitions(compsWithNames);
      } else {
        setCompetitions([]);
      }
    } catch (err) {
      console.error('Error loading competitions:', err);
    } finally {
      setLoadingCompetitions(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setDefaultRole('user');
    setCompetitionAccess([]);
    setError(null);
    onClose();
  };

  const handleSend = async () => {
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await inviteService.sendInvite({
        email: email.trim(),
        defaultRole,
        competitionAccess: competitionAccess.map(c => ({
          competition_id: c.competition.id,
          role: c.role
        }))
      });

      if (onInviteSent) {
        onInviteSent();
      }
      handleClose();
    } catch (err) {
      console.error('Error sending invite:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCompetitionAccess = (competition) => {
    if (!competition) return;
    if (competitionAccess.some(c => c.competition.id === competition.id)) return;

    setCompetitionAccess(prev => [
      ...prev,
      { competition, role: 'viewer' }
    ]);
  };

  const handleRemoveCompetitionAccess = (competitionId) => {
    setCompetitionAccess(prev => prev.filter(c => c.competition.id !== competitionId));
  };

  const handleCompetitionRoleChange = (competitionId, role) => {
    setCompetitionAccess(prev =>
      prev.map(c =>
        c.competition.id === competitionId ? { ...c, role } : c
      )
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddIcon />
          Invite User
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          autoFocus
          margin="dense"
          label="Email Address"
          type="email"
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>System Role</InputLabel>
          <Select
            value={defaultRole}
            label="System Role"
            onChange={(e) => setDefaultRole(e.target.value)}
          >
            <MenuItem value="user">User (Standard access)</MenuItem>
            <MenuItem value="admin">Admin (Can manage users and competitions)</MenuItem>
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            This role is assigned when the user accepts the invite
          </Typography>
        </FormControl>

        <Typography variant="subtitle2" gutterBottom>
          Pre-grant Competition Access (Optional)
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          The user will automatically receive access to these competitions when they sign up
        </Typography>

        {loadingCompetitions ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Autocomplete
            options={competitions.filter(
              c => !competitionAccess.some(ca => ca.competition.id === c.id)
            )}
            getOptionLabel={(option) => option.displayName || option.name || option.id}
            onChange={(e, value) => handleAddCompetitionAccess(value)}
            value={null}
            renderInput={(params) => (
              <TextField {...params} label="Add competition access" size="small" />
            )}
            sx={{ mb: 2 }}
          />
        )}

        {competitionAccess.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {competitionAccess.map(({ competition, role }) => (
              <Box
                key={competition.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1,
                  p: 1,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1
                }}
              >
                <Chip
                  label={competition.displayName || competition.name || competition.id}
                  onDelete={() => handleRemoveCompetitionAccess(competition.id)}
                  size="small"
                />
                <Select
                  value={role}
                  size="small"
                  onChange={(e) => handleCompetitionRoleChange(competition.id, e.target.value)}
                  sx={{ minWidth: 100 }}
                >
                  <MenuItem value="viewer">Viewer</MenuItem>
                  <MenuItem value="editor">Editor</MenuItem>
                  <MenuItem value="owner">Owner</MenuItem>
                </Select>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          disabled={loading || !email.trim()}
          startIcon={loading ? <CircularProgress size={16} /> : <PersonAddIcon />}
        >
          {loading ? 'Sending...' : 'Send Invite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InviteUserDialog;
